/**
 * Client-safe metadata for the 5 postback ad trackers.
 *
 * Mirrors the server-side presets in src/app/services/postback-tracker/
 * but contains only the bits the CRM form needs (display name, docs URL,
 * tracking-domain hint, what the postback URL looks like). Keeping
 * this file separate avoids accidentally importing server-only
 * dependencies into the React bundle.
 *
 * If you change a preset's URL template on the server, update the
 * corresponding `urlTemplatePreview` here so the merchant sees the
 * right preview in the form.
 */

export type PostbackProviderId =
  | 'clickflare'
  | 'voluum'
  | 'binom'
  | 'redtrack'
  | 'clickmagick';

export interface PostbackProviderMeta {
  id: PostbackProviderId;
  displayName: string;
  docsUrl: string;
  trackingDomainPlaceholder: string;
  trackingDomainHint: string;
  /** Human-readable preview of what TagadaPay will fire on order/paid. */
  urlTemplatePreview: string;
  defaultConversionType: string;
  /** Click ID names this tracker reads (URL params first, then cookies). */
  clickIdReads: string[];
}

export const POSTBACK_PROVIDER_META: Readonly<Record<PostbackProviderId, PostbackProviderMeta>> =
  Object.freeze({
    clickflare: {
      id: 'clickflare',
      displayName: 'ClickFlare',
      docsUrl: 'https://help.clickflare.io/en/articles/s2s-postback-urls',
      trackingDomainPlaceholder: 'cft.yourstore.com',
      trackingDomainHint:
        'The custom tracking domain you configured inside ClickFlare. Do not include "https://".',
      urlTemplatePreview:
        'https://{trackingDomain}/cf/postback?cid={clickId}&payout={payout}&txid={orderId}',
      defaultConversionType: 'sale',
      clickIdReads: ['cf_click_id (URL)', 'click_id (URL)', 'cf_click_id (cookie)', 'cfclid (cookie)'],
    },
    voluum: {
      id: 'voluum',
      displayName: 'Voluum',
      docsUrl:
        'https://doc.voluum.com/en/track-conversions-using-s2s-postback-url/parameters-in-postback-urls.html',
      trackingDomainPlaceholder: 'track.yourstore.com',
      trackingDomainHint:
        'Your Voluum tracking domain (the custom domain you CNAMEd to Voluum). Do not include "https://".',
      urlTemplatePreview:
        'https://{trackingDomain}/postback?cid={clickId}&payout={payout}&txid={orderId}&currency=USD',
      defaultConversionType: 'sale',
      clickIdReads: ['cid (URL)', 'click_id (URL)', '_voluum (cookie)', 'voluum (cookie)'],
    },
    binom: {
      id: 'binom',
      displayName: 'Binom',
      docsUrl: 'https://docs.binom.org/postback-url.php',
      trackingDomainPlaceholder: 'track.yourstore.com',
      trackingDomainHint: 'Your Binom tracker domain. Do not include "https://".',
      urlTemplatePreview:
        'https://{trackingDomain}/click.php?cnv_id={clickId}&payout={payout}&cnv_status=sale&cnv_currency=USD',
      defaultConversionType: 'sale',
      clickIdReads: ['clickid (URL)', 'click_id (URL)', '_binom (cookie)', 'binom (cookie)'],
    },
    redtrack: {
      id: 'redtrack',
      displayName: 'RedTrack',
      docsUrl:
        'https://help.redtrack.io/knowledgebase/kb/conversion-tracking/postback-offer-source/',
      trackingDomainPlaceholder: 'go.yourstore.com',
      trackingDomainHint: 'Your RedTrack tracking domain. Do not include "https://".',
      urlTemplatePreview:
        'https://{trackingDomain}/postback?clickid={clickId}&sum={payout}&type=sale',
      defaultConversionType: 'sale',
      clickIdReads: [
        'rtkclickid (URL)',
        'clickid (URL)',
        'click_id (URL)',
        'rtkclickid-store (cookie)',
        '_rtkclickid (cookie)',
      ],
    },
    clickmagick: {
      id: 'clickmagick',
      displayName: 'ClickMagick',
      docsUrl: 'https://www.clickmagick.com/kb/?answer=217',
      trackingDomainPlaceholder: 'www.clickmagick.com',
      trackingDomainHint:
        'Use "www.clickmagick.com" for standard accounts, or your custom vanity domain. Do not include "https://".',
      urlTemplatePreview:
        'https://{trackingDomain}/cm/conversion?cm_id={clickId}&value={amount}',
      defaultConversionType: 'sale',
      clickIdReads: [
        'cmc_tid (URL)',
        'cmc_id (URL)',
        'cmcid (URL)',
        'click_id (URL)',
        '_mck (cookie)',
        'cmcid (cookie)',
      ],
    },
  });

export function getPostbackProviderMeta(id: PostbackProviderId): PostbackProviderMeta {
  return POSTBACK_PROVIDER_META[id];
}
